// Pestaña "Medición" del asistente (/assistant): cerebro operativo, etapa 0.
// Muestra el trabajo pendiente que detecta el cerebro, cuánto consume analizarlo y la comparación
// diaria contra Mercado Libre, más el interruptor del análisis automático. Solo el Administrador
// con alcance total (el servidor lo exige además del permiso).
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api/client';
import {
  fetchResult,
  failureText,
  nextList,
  nextSummary,
  label as tr,
  sellerName,
  SWITCH_FAIL,
  DRAFT_NOTE,
  LATE_NOTE,
  MATTER_STATE,
} from './brainView';
import { decidedCount, type StatCounts } from './decisionsView';

interface Summary {
  enabled: boolean;
  open_by_type: Array<{
    seller_id: number;
    entity_type: string;
    open: number;
    waiting_analysis: number;
    oldest_minutes: number | null;
  }>;
  late: number;
  duplicates: number;
  today: {
    auto_turns: number;
    chat_turns: number;
    tokens: number;
    budget: number;
    limit_cuts: number;
  };
  by_profile_7d: Array<{
    profile: string;
    model: string;
    turns: number;
    ok: number;
    avg_tokens_in: number;
    avg_seconds: number;
    avg_tools: number;
  }>;
  quota: { run: boolean; reason: string | null };
  limits: { per_hour: number; per_day: number; pause_minutes: number };
  audit: Array<{
    day: string;
    seller_id: number;
    metric: string;
    tracked: number | null;
    live: number | null;
    detail: { total_unread?: number; lookback_days?: number } | null;
  }>;
  sellers: Array<{ id: number; name: string }>;
}
/** Lo que la Medición lee de la pestaña Decisiones (solo dos cifras). */
interface DecisionCounts {
  counts: { total: number };
  stats_7d: StatCounts[];
}
interface MatterList {
  rows: Matter[];
  total: number;
}
interface Matter {
  id: number;
  seller_id: number;
  entity_type: string;
  entity_id: string;
  state: string;
  priority_class: number;
  due_at: string | null;
  title: string;
  signals_count: number;
  measure_note: string | null;
  last_signal_at: string;
  closed_at: string | null;
}

const TYPE: Record<string, string> = {
  claim: 'Reclamos',
  question: 'Preguntas',
  post_sale: 'Mensajes de compradores',
};
const METRIC: Record<string, string> = {
  open_claims: 'Reclamos abiertos',
  unanswered_questions: 'Preguntas sin responder',
  unread_conversations: 'Conversaciones con mensajes recientes sin leer',
};
const PROFILE: Record<string, string> = {
  claims: 'Reclamos',
  questions: 'Preguntas',
  post_sale: 'Mensajes de compradores',
  chat: 'Conversación con vos',
  summary: 'Resumen diario',
  auto: 'Análisis automático',
};
const URGENCY: Record<string, string> = {
  0: 'Urgente',
  1: 'Alta',
  2: 'Normal',
  3: 'Baja',
  4: 'Mantenimiento',
};
// Motivos de pausa del medidor de cupo (ya en español); uno desconocido no se muestra.
const PAUSE_REASON: Record<string, string> = {
  'pausa por límite de uso': 'pausa por límite de uso',
  'presupuesto diario agotado': 'presupuesto diario agotado',
  'tope por día': 'tope por día',
  'tope por hora': 'tope por hora',
};
/** Vence sola la confirmación de encender. */
const CONFIRM_MS = 10_000;
const MODEL: Record<string, string> = {
  'claude-opus-5-5': 'el más capaz',
  'claude-sonnet-5': 'el liviano',
};

const fmt = (n: number) => Number(n).toLocaleString('es-AR');
const minutes = (m: number | null) =>
  m == null ? '—' : m < 60 ? `${m} min` : `${Math.round(m / 60)} h`;

export default function BrainTab() {
  const [s, setS] = useState<Summary | null>(null);
  // null = la lista todavía no se pudo leer nunca (no es lo mismo que "no hay asuntos").
  const [list, setList] = useState<MatterList | null>(null);
  const [listWarning, setListWarning] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Cifras de la etapa de propuestas; null = no se pudieron leer (se muestra "—").
  const [dec, setDec] = useState<{ pending: number; decided7d: number } | null>(null);
  const [confirm, setConfirm] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Últimos datos buenos (para no reemplazarlos por vacío si un refresco falla).
  const summaryRef = useRef<Summary | null>(null);
  const listRef = useRef<MatterList | null>(null);

  const clearConfirm = () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    confirmTimer.current = null;
    setConfirm(false);
  };

  // Al desmontar: limpiar el temporizador de la confirmación.
  useEffect(
    () => () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    },
    []
  );

  // Cada respuesta se aplica por separado: un fallo nunca reemplaza datos buenos por vacío.
  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      fetchResult(() => api.get<Summary>('/assistant/brain/summary')),
      fetchResult(() => api.get<MatterList>('/assistant/brain/matters?state=open&limit=100')),
      fetchResult(() => api.get<DecisionCounts>('/assistant/decisions?limit=1')),
    ]);
    setDec(
      c.kind === 'ok'
        ? {
            pending: Number(c.data.counts?.total) || 0,
            decided7d: decidedCount(c.data.stats_7d ?? []),
          }
        : null
    );
    const ns = nextSummary(summaryRef.current, a);
    summaryRef.current = ns.value;
    setS(ns.value);
    setLoadError(ns.error);
    const nl = nextList(listRef.current, b);
    listRef.current = nl.value;
    setList(nl.value);
    setListWarning(nl.warning);
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = async () => {
    if (!s) return;
    setSwitchError(null);
    // Encender pide confirmación en dos clics (vence a los 10 s); apagar es inmediato.
    if (!s.enabled && !confirm) {
      setConfirm(true);
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => {
        confirmTimer.current = null;
        setConfirm(false);
      }, CONFIRM_MS);
      return;
    }
    clearConfirm();
    setBusy(true);
    try {
      const r = await fetchResult(() =>
        api.put<{ enabled: boolean }>('/assistant/brain/switch', { enabled: !s.enabled })
      );
      if (r.kind === 'ok') await load();
      else setSwitchError(failureText(r, SWITCH_FAIL));
    } finally {
      setBusy(false);
    }
  };

  if (!s) {
    return loadError ? (
      <div className="p-4 text-sm text-red-600">{loadError}</div>
    ) : (
      <div className="p-4 text-sm text-gray-500">Cargando…</div>
    );
  }

  const used =
    s.today.budget > 0 ? Math.min(100, Math.round((s.today.tokens / s.today.budget) * 100)) : 0;

  return (
    <div className="space-y-6 p-4">
      {loadError && (
        <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
          {loadError}
        </div>
      )}

      <section className="rounded border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Etapa de medición</h2>
            <p className="text-sm text-gray-600">
              El cerebro detecta el trabajo pendiente, lo analiza y, para preguntas y mensajes de
              compradores, propone respuestas que esperan tu sí en la pestaña Decisiones. No envía
              nada por su cuenta.
            </p>
          </div>
          <button
            onClick={() => void toggle()}
            disabled={busy}
            className={`rounded px-3 py-2 text-sm font-medium text-white ${s.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} disabled:opacity-50`}
          >
            {s.enabled
              ? 'Apagar el análisis automático'
              : confirm
                ? '¿Seguro? Encender'
                : 'Encender el análisis automático'}
          </button>
        </div>
        <p className="mt-2 text-sm">
          Estado: <b>{s.enabled ? 'encendido' : 'apagado'}</b>
          {s.enabled && !s.quota.run
            ? s.quota.reason && PAUSE_REASON[s.quota.reason]
              ? ` — en pausa (${PAUSE_REASON[s.quota.reason]})`
              : ' — en pausa'
            : ''}
        </p>
        {switchError && (
          <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
            {switchError}
          </div>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Como máximo {fmt(s.limits.per_hour)} análisis por hora y {fmt(s.limits.per_day)} por día.
          Si el plan de suscripción avisa que se llegó al límite, el análisis se pausa{' '}
          {fmt(s.limits.pause_minutes)} minutos solo.
        </p>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Stat label="Asuntos fuera de plazo" value={fmt(s.late)} bad={s.late > 0} />
          <p className="mt-1 text-xs text-gray-500">{LATE_NOTE}</p>
        </div>
        <Stat label="Asuntos repetidos" value={fmt(s.duplicates)} bad={s.duplicates > 0} />
        <Stat label="Análisis automáticos de hoy" value={fmt(s.today.auto_turns)} />
        <Stat
          label="Cortes por límite del plan, hoy"
          value={fmt(s.today.limit_cuts)}
          bad={s.today.limit_cuts > 0}
        />
        <Stat
          label="Propuestas esperando tu decisión"
          value={dec ? fmt(dec.pending) : '—'}
        />
        <Stat label="Decisiones tomadas en 7 días" value={dec ? fmt(dec.decided7d) : '—'} />
      </section>

      <section className="rounded border border-gray-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold">Consumo de hoy</h3>
        <div className="h-3 w-full overflow-hidden rounded bg-gray-100">
          <div
            className={`h-3 ${used >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: `${used}%` }}
          />
        </div>
        <p className="mt-1 text-sm text-gray-600">
          {fmt(s.today.tokens)} de {fmt(s.today.budget)} unidades ({used} %). Suma lo que el
          asistente lee y lo que escribe. Incluye tus conversaciones ({fmt(s.today.chat_turns)}{' '}
          respuestas hoy). Desde el 60 % solo se analizan los asuntos urgentes.
        </p>
      </section>

      <Table
        title="Trabajo pendiente detectado"
        head={['Empresa', 'Tipo', 'Abiertos', 'Esperando análisis', 'El más viejo espera']}
        rows={s.open_by_type.map((r) => [
          sellerName(s.sellers, r.seller_id),
          tr(TYPE, r.entity_type),
          fmt(r.open),
          fmt(r.waiting_analysis),
          minutes(r.oldest_minutes),
        ])}
        empty="Todavía no se detectó trabajo pendiente."
      />

      <Table
        title="Cuánto cuesta analizar cada tipo (últimos 7 días)"
        head={[
          'Tipo de trabajo',
          'Modelo',
          'Análisis',
          'Terminados bien',
          'Consumo promedio de lectura (unidades)',
          'Tiempo promedio',
          'Consultas promedio',
        ]}
        rows={s.by_profile_7d.map((r) => [
          tr(PROFILE, r.profile),
          tr(MODEL, r.model),
          fmt(r.turns),
          fmt(r.ok),
          fmt(Number(r.avg_tokens_in)),
          `${r.avg_seconds} s`,
          String(r.avg_tools),
        ])}
        empty="Todavía no hay análisis registrados."
      />

      <Table
        title="Comparación diaria contra Mercado Libre"
        head={[
          'Día',
          'Empresa',
          'Medida',
          'Lo que sigue el cerebro',
          'Lo que dice Mercado Libre',
          'Diferencia',
        ]}
        rows={s.audit.map((r) => [
          r.day,
          sellerName(s.sellers, r.seller_id),
          <div key="m">
            {tr(METRIC, r.metric)}
            {r.metric === 'unread_conversations' && r.detail?.total_unread != null && (
              <div className="text-xs text-gray-500">
                En total hay {fmt(r.detail.total_unread)} sin leer contando las de ventas viejas
              </div>
            )}
          </div>,
          r.tracked == null ? '—' : fmt(r.tracked),
          r.live == null ? 'no se pudo consultar' : fmt(r.live),
          r.tracked == null || r.live == null ? '—' : fmt(r.tracked - r.live),
        ])}
        empty="La primera comparación se hace a las 9 de la mañana (hora de Nueva York)."
      />

      <section className="rounded border border-gray-200 bg-white p-4">
        {listWarning && (
          <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
            {listWarning}
          </div>
        )}
        <h3 className="mb-2 text-sm font-semibold">
          Asuntos abiertos{list ? ` (${fmt(list.total)})` : ''}
          {list && list.total > list.rows.length && (
            <span className="ml-1 font-normal text-gray-500">
              — se muestran los {fmt(list.rows.length)} más urgentes
            </span>
          )}
        </h3>
        {list && list.rows.length === 0 && !listWarning && (
          <p className="text-sm text-gray-500">No hay asuntos abiertos.</p>
        )}
        <ul className="divide-y divide-gray-100">
          {(list?.rows ?? []).map((m) => (
            <li key={m.id} className="py-2">
              <button
                className="w-full text-left"
                onClick={() => setOpen(open === m.id ? null : m.id)}
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">
                    {tr(URGENCY, m.priority_class)}
                  </span>
                  <span className="text-gray-500">{sellerName(s.sellers, m.seller_id)}</span>
                  <span className="font-medium">{m.title}</span>
                  <span className="ml-auto text-xs text-gray-500">{tr(MATTER_STATE, m.state)}</span>
                </div>
              </button>
              {open === m.id && (
                <div className="mt-2">
                  {m.measure_note != null && (
                    <p className="mb-1 text-xs text-gray-500">{DRAFT_NOTE}</p>
                  )}
                  <pre className="whitespace-pre-wrap rounded bg-gray-50 p-3 text-sm text-gray-800">
                    {m.measure_note ?? 'Todavía no se analizó.'}
                  </pre>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div
      className={`rounded border p-3 ${bad ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}
    >
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold ${bad ? 'text-red-700' : ''}`}>{value}</div>
    </div>
  );
}

function Table({
  title,
  head,
  rows,
  empty,
}: {
  title: string;
  head: string[];
  rows: ReactNode[][];
  empty: string;
}) {
  return (
    <section className="rounded border border-gray-200 bg-white p-4">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                {head.map((h) => (
                  <th key={h} className="px-2 py-1 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  {r.map((c, j) => (
                    <td key={j} className="px-2 py-1">
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
